import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const GUEST_API_CALL_LIMIT = 5;
const REGISTERED_USER_API_CALL_LIMIT = 20;
const STORAGE_KEY = 'ai_travel_api_calls';
const DATE_KEY = 'ai_travel_api_calls_date';

export const useApiCallLimit = () => {
  const { isSignedIn } = useAuth();
  const [callCount, setCallCount] = useState<number>(0);
  const [limitReached, setLimitReached] = useState<boolean>(false);

  // Determine the API limit based on user authentication status
  const API_CALL_LIMIT = isSignedIn ? REGISTERED_USER_API_CALL_LIMIT : GUEST_API_CALL_LIMIT;

  useEffect(() => {
    // Get today's date string
    const today = new Date().toDateString();
    
    // Check if it's a new day
    const storedDate = localStorage.getItem(DATE_KEY);
    const isNewDay = storedDate !== today;

    if (isNewDay) {
      // Reset count for new day
      setCallCount(0);
      setLimitReached(false);
      localStorage.setItem(DATE_KEY, today);
      localStorage.removeItem(STORAGE_KEY);
    } else {
      // Load call count from localStorage for the current day
      const storedCount = localStorage.getItem(STORAGE_KEY);
      const count = storedCount ? parseInt(storedCount, 10) : 0;
      setCallCount(count);
      setLimitReached(count >= API_CALL_LIMIT);
    }
  }, [API_CALL_LIMIT]);

  const incrementCallCount = () => {
    const newCount = callCount + 1;
    setCallCount(newCount);
    localStorage.setItem(STORAGE_KEY, newCount.toString());
    
    if (newCount >= API_CALL_LIMIT) {
      setLimitReached(true);
    }
  };

  const resetCallCount = () => {
    setCallCount(0);
    setLimitReached(false);
    localStorage.removeItem(STORAGE_KEY);
    // Update the date to today to prevent automatic reset
    const today = new Date().toDateString();
    localStorage.setItem(DATE_KEY, today);
  };

  const getRemainingCalls = () => {
    return Math.max(0, API_CALL_LIMIT - callCount);
  };

  return {
    callCount,
    limitReached,
    remainingCalls: getRemainingCalls(),
    incrementCallCount,
    resetCallCount,
    API_CALL_LIMIT
  };
}; 